import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import {
  bannerElementOwners,
  bannerLetterLabel,
  describeCellSig,
  describeSigArrow,
  describeSigElement,
  describeSigLetters,
  formatCell,
  letterRuns,
  sigElementAt,
  sigLegendLines,
  sigSourceOf,
  STATISTIC_LABEL,
} from "utils/tableModel"
import type {
  AxisLeaf,
  CellSig,
  HeaderNode,
  ModelCell,
  SigMeta,
  TableModel,
} from "utils/tableModel"
import {
  applySigStyle,
  effectiveNumberFormats,
  ptToPx,
  resolveCellStyle,
  rowBands,
  toContentCss,
  toCss,
} from "../style/resolveCellStyle"
import type { CellContext } from "../style/resolveCellStyle"
import type {
  CellStyleSpec,
  ResolvedStyle,
  TableStyle,
} from "../style/tableStyle.types"

export interface DataTableSelection {
  /** Ids (`AxisLeaf.id`) of the selected column elements. */
  selectedAcross: Set<string>
  /** Ids (`AxisLeaf.id`) of the selected row elements. */
  selectedDown: Set<string>
  onToggle: (axis: "across" | "down", id: string) => void
  /** Whether clicking a leaf header toggles it (Totals never toggles). */
  selectable: boolean
  /** Per-axis switch on top of `selectable` (both axes by default). */
  selectableAxes?: { across: boolean; down: boolean }
}

export interface DataTableProps {
  model: TableModel
  style: TableStyle
  /**
   * Significance display (tables run with `SIG:`), by default the style's
   * `sig.letterPlacement.screen`:
   * - "inline": letters in small type after the value, arrows next to it;
   *   the engine's `sig` rows/columns are hidden;
   * - "row": the `sig` rows/columns are shown, with the arrows added to them
   *   (the Excel layout);
   * - "off": the `sig` rows/columns are hidden and nothing else is drawn.
   * Except with "off", tables with significance also get the column letters
   * (a row under the banner, or a column beside it when the banner runs
   * down), tints, greyed low-base elements, the legend and a hover
   * explanation.
   */
  sigDisplay?: "inline" | "row" | "off"
  selection?: DataTableSelection
  /** Multiplies the style's font size and the cell padding. */
  fontScale?: number
  /** Cell padding in px on all sides; overrides the density padding (not scaled). */
  cellPadding?: number
  /**
   * Table width: "content" (default) sizes to the content, "fill" spans the
   * container, "stretch" spans it with fixed-width columns.
   */
  layout?: "content" | "fill" | "stretch"
  /**
   * Makes the data cells clickable (pointer, keyboard focus with Enter or
   * Space, a focus ring): called with the cell's row and column leaves.
   * Cells of significance letters are not numbers and stay as they are.
   * `shown` is the number as the cell shows it.
   */
  onCellClick?: (down: AxisLeaf, across: AxisLeaf, shown: string) => void
}

/** A header node with its position among the visible leaves. */
interface PlacedNode {
  node: HeaderNode
  level: number
  /** Number of visible leaves under the node. */
  count: number
  /** Index of the first visible leaf. */
  first: number
  isLeafLevel: boolean
}

/**
 * One cell of the column-letters line: the visible leaves of one banner
 * element (`element` -1: a leaf no element covers, left blank).
 */
interface LetterSlot {
  element: number
  first: number
  /** Model index of the slot's last leaf, shown or not. */
  last: number
  count: number
}

interface CellOptions {
  colSpan?: number
  rowSpan?: number
  scope?: "col" | "row"
  selected?: boolean
  onClick?: () => void
  title?: string
  coords?: { y: number; x: number }
  className?: string
  /** Banner element index, for the hover explanation of a letter cell. */
  sigElement?: number
  /** A data cell that opens something: focusable, and Enter or Space clicks it. */
  activate?: () => void
}

interface TooltipState {
  text: string
  left: number
  top: number
  above: boolean
}

/** A row or column made only of significance letters (the `sig` statistic). */
const isLettersLine = (cells: (ModelCell | undefined)[]): boolean =>
  cells.length > 0 && cells.every((cell) => cell?.role === "letters")

/** Pre-order walk keeping nodes with at least one visible leaf. */
const placeNodes = (tree: HeaderNode[], visible: boolean[]): PlacedNode[] => {
  const placed: PlacedNode[] = []
  const walk = (nodes: HeaderNode[], level: number): void => {
    nodes.forEach((node) => {
      let count = 0
      let first = -1
      for (let i = node.span[0]; i < node.span[1]; i++) {
        if (!visible[i]) continue
        if (first < 0) first = i
        count++
      }
      if (count === 0) return
      placed.push({
        node,
        level,
        count,
        first,
        isLeafLevel: node.children.length === 0,
      })
      walk(node.children, level + 1)
    })
  }
  walk(tree, 0)
  return placed
}

/** Groups the visible banner leaves by the element that owns them. */
const letterSlots = (visible: boolean[], owners: number[]): LetterSlot[] => {
  const slots: LetterSlot[] = []
  let i = 0
  while (i < visible.length) {
    if (!visible[i]) {
      i++
      continue
    }
    const element = owners[i] ?? -1
    let count = 1
    let j = i + 1
    if (element >= 0) {
      while (j < visible.length && owners[j] === element) {
        if (visible[j]) count++
        j++
      }
    }
    slots.push({ element, first: i, last: j - 1, count })
    i = j
  }
  return slots
}

/** Totals and unlabelled elements are never selectable. */
const isSelectableLeaf = (leaf: AxisLeaf | undefined): leaf is AxisLeaf =>
  leaf !== undefined && !leaf.isTotal && leaf.id !== ""

/** Rounds a pixel value to 2 decimals. */
const px = (value: number): string => `${Math.round(value * 100) / 100}px`

/**
 * Fingerprint of what decides the cell edges: the section borders, the
 * position rules, the band rule and the sig display (the rows shown). The
 * table is keyed on it, so the cells mount afresh when it changes: a cell sets
 * the `border` shorthand and then the `borderBottom` / `borderTop` longhands,
 * and a re-render re-applies only the values that changed, so a new `border`
 * would wipe an unchanged `borderBottom`.
 */
const edgesKey = (
  style: TableStyle,
  display: NonNullable<DataTableProps["sigDisplay"]>
): string => {
  const edges = (spec?: CellStyleSpec) => [spec?.border, spec?.borderBottom]
  return JSON.stringify([
    style.rules?.top,
    style.rules?.headerBottom,
    style.rules?.bottom,
    style.bandRule,
    edges(style.header),
    edges(style.subHeader),
    edges(style.stub),
    edges(style.statLabel),
    edges(style.body),
    edges(style.totals),
    display,
  ])
}

/** Room kept under a cell before the tooltip flips above it. */
const TOOLTIP_FLIP_PX = 80

/** Present for screen readers, invisible on screen. */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
}

/**
 * Renders a TableModel as an HTML table: both header trees with
 * colSpan/rowSpan, the statistic labels, numbers via `formatCell` (with the
 * style's decimals) and every cell styled through `resolveCellStyle`, merged
 * cells with their span and the row bands of the rows shown, so the style's
 * `rules` and `bandRule` land on the last shown row, never a hidden `sig` row.
 *
 * The grid mirrors the engine layout: `freeze.y` header rows (column header
 * levels, plus the statistic row under /T) and `freeze.x` stub columns (row
 * header levels, plus the statistic column when stats run down). With
 * significance, the column letters add one header row under the column
 * headers (banner across) or one stub column after the row headers (banner
 * down: DTAB, or XTAB under /T).
 */
const DataTable: React.FC<DataTableProps> = ({
  model,
  style,
  sigDisplay,
  selection,
  fontScale,
  cellPadding,
  layout = "content",
  onCellClick,
}) => {
  const scale = fontScale && fontScale > 0 ? fontScale : 1
  const numberFormats = effectiveNumberFormats(style)
  const display = sigDisplay ?? style.sig.letterPlacement.screen
  // Remounts the cells when their edges change (see `edgesKey`).
  const tableKey = useMemo(() => edgesKey(style, display), [style, display])

  const grid = useMemo(() => {
    const { x: fx, y: fy } = model.freeze
    const hasStatColumn = model.statAxis === "down" && fx >= 1
    const hasStatRow = model.statAxis === "across" && fy >= 1
    const acrossLevels = Math.max(0, fy - (hasStatRow ? 1 : 0))
    const downLevels = Math.max(0, fx - (hasStatColumn ? 1 : 0))

    const hideLetters = display !== "row"
    const downVisible = model.down.leaves.map(
      (_, r) => !(hideLetters && isLettersLine(model.cells[r] ?? []))
    )
    // Band and table-end edges land on the last shown row, never a hidden
    // `sig` row.
    const bands = rowBands(model, downVisible)
    const acrossVisible = model.across.leaves.map(
      (_, c) =>
        !(hideLetters && isLettersLine(model.cells.map((row) => row[c])))
    )

    const acrossByLevel = new Map<number, PlacedNode[]>()
    if (acrossLevels > 0) {
      placeNodes(model.across.tree, acrossVisible)
        .filter((placed) => placed.level < acrossLevels)
        .forEach((placed) => {
          const list = acrossByLevel.get(placed.level) ?? []
          list.push(placed)
          acrossByLevel.set(placed.level, list)
        })
    }

    const downByFirst = new Map<number, PlacedNode[]>()
    if (downLevels > 0) {
      placeNodes(model.down.tree, downVisible)
        .filter((placed) => placed.level < downLevels)
        .forEach((placed) => {
          const list = downByFirst.get(placed.first) ?? []
          list.push(placed)
          downByFirst.set(placed.first, list)
        })
    }

    // Significance: the column letters line runs along the banner axis.
    const sig: SigMeta | undefined =
      display !== "off" && (model.significance?.elements.length ?? 0) > 0
        ? model.significance
        : undefined
    const lettersAxis = sig?.bannerAxis
    const slots = sig
      ? letterSlots(
          lettersAxis === "across" ? acrossVisible : downVisible,
          bannerElementOwners(model)
        )
      : []

    return {
      fx,
      fy,
      hasStatColumn,
      hasStatRow,
      acrossLevels,
      downLevels,
      downVisible,
      bands,
      acrossVisible,
      acrossByLevel,
      downByFirst,
      sig,
      extraRow: lettersAxis === "across" ? 1 : 0,
      extraCol: lettersAxis === "down" ? 1 : 0,
      slots,
      downSlotsByFirst: new Map(
        lettersAxis === "down" ? slots.map((slot) => [slot.first, slot]) : []
      ),
    }
  }, [model, display])

  const {
    fx,
    fy,
    hasStatColumn,
    hasStatRow,
    acrossLevels,
    downLevels,
    downVisible,
    bands,
    acrossVisible,
    acrossByLevel,
    downByFirst,
    sig,
    extraRow,
    extraCol,
    slots,
    downSlotsByFirst,
  } = grid

  // One tooltip for the whole table, driven by mouseover on the cells.
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const anchorRef = useRef<Element | null>(null)

  useEffect(() => {
    setTooltip(null)
    anchorRef.current = null
  }, [model, display])

  useEffect(() => {
    if (!tooltip) return
    const hide = (): void => {
      setTooltip(null)
      anchorRef.current = null
    }
    window.addEventListener("scroll", hide, true)
    return () => window.removeEventListener("scroll", hide, true)
  }, [tooltip])

  const padding =
    cellPadding !== undefined
      ? px(cellPadding)
      : style.density === "compact"
        ? `${px(6 * scale)} ${px(8 * scale)}`
        : px(8 * scale)

  /** Resolves a screen cell with the shown-row bands. */
  const resolveAt = (y: number, x: number, ctx: CellContext = {}): ResolvedStyle =>
    resolveCellStyle(model, y, x, style, "screen", { bands, ...ctx })

  const renderCell = (
    Tag: "th" | "td",
    key: string,
    resolved: ResolvedStyle,
    content: React.ReactNode,
    options: CellOptions = {}
  ): React.ReactElement => {
    const activate = options.activate
    const clickable = Boolean(options.onClick || activate)
    const cellStyle: React.CSSProperties = {
      ...toCss({ ...resolved, fontSizePt: resolved.fontSizePt * scale }),
      padding,
      ...(options.selected ? { backgroundColor: style.selection.bg } : {}),
      ...(clickable ? { cursor: "pointer" } : {}),
      ...(selection ? { transition: "background-color 0.2s" } : {}),
    }
    const contentStyle = toContentCss(resolved)
    return (
      <Tag
        key={key}
        colSpan={options.colSpan && options.colSpan > 1 ? options.colSpan : undefined}
        rowSpan={options.rowSpan && options.rowSpan > 1 ? options.rowSpan : undefined}
        scope={options.scope}
        style={cellStyle}
        title={options.title}
        onClick={options.onClick ?? activate}
        onKeyDown={
          activate
            ? (event: React.KeyboardEvent) => {
                if (event.key !== "Enter" && event.key !== " ") return
                event.preventDefault()
                activate()
              }
            : undefined
        }
        tabIndex={activate ? 0 : undefined}
        aria-selected={options.selected ? true : undefined}
        className={
          activate
            ? [options.className, "dq-data-table__cell--action"].filter(Boolean).join(" ")
            : options.className
        }
        data-y={options.coords?.y}
        data-x={options.coords?.x}
        data-sig-element={options.sigElement}
      >
        {contentStyle ? <div style={contentStyle}>{content}</div> : content}
      </Tag>
    )
  }

  const toggleHandler = (
    axis: "across" | "down",
    leaf: AxisLeaf | undefined
  ): (() => void) | undefined =>
    selection?.selectable &&
    (selection.selectableAxes?.[axis] ?? true) &&
    isSelectableLeaf(leaf)
      ? () => selection.onToggle(axis, leaf.id)
      : undefined

  const oneLineTitle = (resolved: ResolvedStyle, text: string) =>
    resolved.wrap ? undefined : text

  // ---------------------------------------------------------------------------
  // Significance content
  // ---------------------------------------------------------------------------

  /**
   * Letters in upper/lower case styles; superscript when inline. Screen
   * readers get the meaning in words instead of the bare letters.
   */
  const renderLetters = (cellSig: CellSig, inline: boolean): React.ReactNode => {
    const runs = letterRuns(cellSig)
    if (runs.length === 0 || !sig) return null
    const Tag = inline ? "sup" : "span"
    return (
      <>
        <Tag
          className="dq-sig-letters"
          aria-hidden="true"
          style={
            inline
              ? { fontSize: `${style.sig.letterSizeRatio}em`, marginLeft: "0.15em" }
              : undefined
          }
        >
          {runs.map((run, index) => (
            <span
              key={index}
              className={`dq-sig-${run.level}`}
              style={{
                color: style.sig[run.level].text,
                fontWeight: style.sig[run.level].bold ? "bold" : "normal",
              }}
            >
              {run.text}
            </span>
          ))}
        </Tag>
        <span className="dq-sig-sr" style={VISUALLY_HIDDEN}>
          {` ${describeSigLetters(sig, cellSig)}`}
        </span>
      </>
    )
  }

  const renderArrow = (cellSig: CellSig, inline: boolean): React.ReactNode => {
    const vsRest = cellSig.vsRest
    if (!vsRest || !sig) return null
    const arrow = vsRest.dir === "up" ? style.sig.arrowUp : style.sig.arrowDown
    return (
      <span
        className={`dq-sig-arrow dq-sig-arrow-${vsRest.dir}`}
        role="img"
        aria-label={describeSigArrow(sig, vsRest)}
        style={{
          color: arrow.color,
          marginLeft: "0.2em",
          ...(inline ? { fontSize: `${style.sig.letterSizeRatio}em` } : {}),
        }}
      >
        {arrow.glyph}
      </span>
    )
  }

  /** Content of a data cell of a table with significance. */
  const renderSigCell = (
    cell: ModelCell,
    r: number,
    c: number,
    text: string
  ): React.ReactNode => {
    let content: React.ReactNode
    if (cell.role === "letters" && display === "row") {
      const source = sigSourceOf(model, r, c)
      const sourceSig = source
        ? model.cells[source.row][source.col].sig
        : undefined
      content = sourceSig ? (
        <>
          {renderLetters(sourceSig, false)}
          {renderArrow(sourceSig, false)}
        </>
      ) : (
        text
      )
    } else if (cell.sig && display === "inline") {
      content = (
        <>
          <span className="dq-sig-value">{text}</span>
          {renderLetters(cell.sig, true)}
          {renderArrow(cell.sig, true)}
        </>
      )
    } else {
      content = text
    }
    const lowBase = sigElementAt(model, r, c)?.lowBase === true
    return lowBase ? (
      <span className="dq-sig-low-base" style={{ opacity: style.lowBase.opacity }}>
        {content}
      </span>
    ) : (
      content
    )
  }

  /**
   * The column-letters cell of a banner element ("(A)", "(E)*"). It takes
   * the look of the header cell at (`y`, `x`), with `ctx` for its own edges:
   * the letters row takes the header rule when it ends the header block, the
   * letters column the band edges of the rows it spans.
   */
  const renderLetterSlot = (
    key: string,
    slot: LetterSlot,
    y: number,
    x: number,
    span: { colSpan?: number; rowSpan?: number },
    ctx: CellContext
  ): React.ReactElement => {
    const element =
      slot.element >= 0 ? sig?.elements[slot.element] : undefined
    // A colSpan marks the letters row (banner across), never the table's
    // first row: the top rule of the header cell it copies stays off.
    const lettersRow = span.colSpan !== undefined
    const base = resolveAt(y, x, ctx)
    const resolved: ResolvedStyle = {
      ...base,
      bold: false,
      ...(lettersRow ? { borderTop: undefined } : {}),
      ...(element?.lowBase
        ? {
            color: style.lowBase.textColor,
            italic: base.italic || style.lowBase.italic,
          }
        : {}),
    }
    return renderCell(
      "th",
      key,
      resolved,
      element ? bannerLetterLabel(element, style.lowBase.marker) : "",
      {
        ...span,
        scope: lettersRow ? "col" : "row",
        className: `dq-sig-letter${element?.lowBase ? " dq-sig-low-base" : ""}`,
        ...(element ? { sigElement: slot.element } : {}),
      }
    )
  }

  // ---------------------------------------------------------------------------
  // Header rows
  // ---------------------------------------------------------------------------

  const headerRowCount = fy + extraRow
  const titleCols = hasStatColumn ? fx - 1 : fx
  const titleRows = hasStatRow ? fy - 1 : fy
  const headerRows: React.ReactElement[] = []
  for (let hr = 0; hr < headerRowCount; hr++) {
    const cells: React.ReactElement[] = []
    const isLettersRow = extraRow === 1 && hr === acrossLevels
    const isStatRow = hasStatRow && hr === headerRowCount - 1

    // Corner: the title block plus the "Statistic" label (its own column when
    // stats run down, its own row when they run across). The block also
    // covers the column-letters row or column.
    if (fx + extraCol > 0 && hr === 0) {
      const cornerCols = titleCols + extraCol
      const cornerRows = titleRows + extraRow
      if (cornerCols > 0 && cornerRows > 0) {
        cells.push(
          renderCell(
            "th",
            "corner-title",
            // Under /T the "Statistic" row below it ends the header block.
            resolveAt(0, 0, { rowSpan: titleRows, headerRule: titleRows === fy }),
            titleCols > 0 && titleRows > 0 ? (model.title ?? "") : "",
            { colSpan: cornerCols, rowSpan: cornerRows }
          )
        )
      }
      if (hasStatColumn) {
        cells.push(
          renderCell(
            "th",
            "corner-stat",
            // Spans every header row, from the top rule to the header rule.
            resolveAt(0, fx - 1, { rowSpan: fy, headerRule: true }),
            STATISTIC_LABEL,
            { rowSpan: fy + extraRow }
          )
        )
      }
    }
    if (fx + extraCol > 0 && isStatRow) {
      cells.push(
        renderCell(
          "th",
          "corner-stat",
          resolveAt(fy - 1, fx - 1),
          STATISTIC_LABEL,
          { colSpan: fx + extraCol }
        )
      )
    }

    if (hr < acrossLevels) {
      for (const placed of acrossByLevel.get(hr) ?? []) {
        const x = fx + placed.node.span[0]
        // Only leaf-level headers (one element) are selectable.
        const leaf = placed.isLeafLevel
          ? model.across.leaves[placed.node.span[0]]
          : undefined
        const rows = placed.isLeafLevel ? acrossLevels - hr : 1
        cells.push(
          renderCell(
            "th",
            `h-${hr}-${placed.node.span[0]}`,
            resolveAt(hr, x, {
              rowSpan: rows,
              // The banner never ends the header block over a letters row.
              ...(extraRow === 1 ? { headerRule: false } : {}),
            }),
            placed.node.label,
            {
              scope: "col",
              colSpan: placed.count,
              rowSpan: rows,
              selected: Boolean(leaf && selection?.selectedAcross.has(leaf.id)),
              onClick: toggleHandler("across", leaf),
            }
          )
        )
      }
    } else if (isLettersRow) {
      slots.forEach((slot) => {
        cells.push(
          renderLetterSlot(
            `l-${slot.first}`,
            slot,
            Math.max(0, acrossLevels - 1),
            fx + slot.first,
            { colSpan: slot.count },
            // The header rule goes under the last header row: the letters
            // row, or under /T the statistic row that follows it.
            { headerRule: !hasStatRow }
          )
        )
      })
    } else if (isStatRow) {
      model.across.leaves.forEach((leaf, c) => {
        if (!acrossVisible[c]) return
        cells.push(
          renderCell(
            "th",
            `s-${c}`,
            resolveAt(fy - 1, fx + c),
            leaf.stat ?? "",
            { scope: "col" }
          )
        )
      })
    }

    headerRows.push(<tr key={`hr-${hr}`}>{cells}</tr>)
  }

  // ---------------------------------------------------------------------------
  // Body rows
  // ---------------------------------------------------------------------------

  const bodyRows: React.ReactElement[] = []
  /** Opens the cell (`onCellClick`): data cells with a number, never a letters cell. */
  const cellAction = (
    downLeaf: AxisLeaf,
    c: number,
    cell: ModelCell | undefined,
    shown: string
  ): (() => void) | undefined => {
    const acrossLeaf = model.across.leaves[c]
    if (!onCellClick || !cell || cell.role === "letters" || !acrossLeaf)
      return undefined
    return () => onCellClick(downLeaf, acrossLeaf, shown)
  }

  model.down.leaves.forEach((downLeaf, r) => {
    if (!downVisible[r]) return
    const y = fy + r
    const rowSelected = Boolean(selection?.selectedDown.has(downLeaf.id))
    const cells: React.ReactElement[] = []

    // Stub cells of the header nodes whose first visible row is this one.
    for (const placed of downByFirst.get(r) ?? []) {
      // Only leaf-level headers (one element) are selectable.
      const leaf = placed.isLeafLevel
        ? model.down.leaves[placed.node.span[0]]
        : undefined
      // The model span (hidden rows included) reaches the band edges.
      const resolved = resolveAt(fy + placed.node.span[0], placed.level, {
        rowSpan: placed.node.span[1] - placed.node.span[0],
      })
      cells.push(
        renderCell("th", `d-${placed.level}-${r}`, resolved, placed.node.label, {
          scope: "row",
          rowSpan: placed.count,
          colSpan: placed.isLeafLevel ? downLevels - placed.level : 1,
          selected: Boolean(leaf && selection?.selectedDown.has(leaf.id)),
          onClick: toggleHandler("down", leaf),
          title: oneLineTitle(resolved, placed.node.label),
        })
      )
    }

    // Column letters beside the row headers (banner down).
    const downSlot = extraCol === 1 ? downSlotsByFirst.get(r) : undefined
    if (downSlot) {
      cells.push(
        renderLetterSlot(
          `l-${r}`,
          downSlot,
          fy + downSlot.first,
          Math.max(0, downLevels - 1),
          { rowSpan: downSlot.count },
          { rowSpan: downSlot.last - downSlot.first + 1 }
        )
      )
    }

    if (hasStatColumn) {
      const resolved = resolveAt(y, fx - 1)
      // A leaf with its own label (a dashboard measure's unit) shows it.
      const stat = downLeaf.statLabel ?? downLeaf.stat ?? ""
      cells.push(
        renderCell("td", `st-${r}`, resolved, stat, {
          selected: rowSelected,
          title: oneLineTitle(resolved, stat),
        })
      )
    }

    model.across.leaves.forEach((_, c) => {
      if (!acrossVisible[c]) return
      const cell = model.cells[r]?.[c]
      const text = cell
        ? formatCell(cell, numberFormats, style.numberDecimals)
        : ""
      const x = fx + c
      const base = resolveAt(y, x)
      if (!sig || !cell) {
        cells.push(
          renderCell("td", `c-${r}-${c}`, base, text, {
            selected: rowSelected,
            title: oneLineTitle(base, text),
            coords: { y, x },
            activate: cellAction(downLeaf, c, cell, text),
          })
        )
        return
      }
      const resolved = applySigStyle(base, model, y, x, style)
      const explained = Boolean(cell.sig) || cell.role === "letters"
      cells.push(
        renderCell("td", `c-${r}-${c}`, resolved, renderSigCell(cell, r, c, text), {
          selected: rowSelected,
          // The hover explanation replaces the native title on tested cells.
          title: explained ? undefined : oneLineTitle(resolved, text),
          coords: { y, x },
          activate: cellAction(downLeaf, c, cell, text),
        })
      )
    })

    bodyRows.push(<tr key={`br-${r}`}>{cells}</tr>)
  })

  const tableStyle: React.CSSProperties = {
    borderCollapse: "collapse",
    width: layout === "content" ? "auto" : "100%",
    tableLayout: layout === "stretch" ? "fixed" : "auto",
    ...(style.body.bg !== undefined ? { backgroundColor: style.body.bg } : {}),
  }

  // ---------------------------------------------------------------------------
  // Hover explanation (one delegated tooltip)
  // ---------------------------------------------------------------------------

  const explain = (target: Element): string | null => {
    const element = target.getAttribute("data-sig-element")
    if (element !== null) return describeSigElement(model, Number(element))
    const y = Number(target.getAttribute("data-y"))
    const x = Number(target.getAttribute("data-x"))
    if (!Number.isInteger(y) || !Number.isInteger(x)) return null
    return describeCellSig(model, y, x, numberFormats, style.numberDecimals)
  }

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target =
      event.target instanceof Element
        ? event.target.closest("td[data-y][data-x], th[data-sig-element]")
        : null
    if (target === anchorRef.current) return
    anchorRef.current = target
    const text = target ? explain(target) : null
    if (!target || !text) {
      setTooltip(null)
      return
    }
    const rect = target.getBoundingClientRect()
    const above =
      typeof window !== "undefined" &&
      rect.bottom + TOOLTIP_FLIP_PX > window.innerHeight
    setTooltip({
      text,
      left: rect.left + rect.width / 2,
      top: above ? rect.top - 6 : rect.bottom + 6,
      above,
    })
  }

  const handleMouseLeave = (): void => {
    anchorRef.current = null
    setTooltip(null)
  }

  // The engine's legend, the display-only lines for "*" and the shading, then
  // the notes.
  const legendLines = sig ? sigLegendLines(model, style) : []

  return (
    <div
      className="dq-data-table"
      style={layout === "content" ? undefined : { width: "100%" }}
      onMouseOver={sig ? handleMouseOver : undefined}
      onMouseLeave={sig ? handleMouseLeave : undefined}
    >
      <table key={tableKey} style={tableStyle}>
        {headerRows.length > 0 && <thead>{headerRows}</thead>}
        <tbody>{bodyRows}</tbody>
      </table>
      {legendLines.length > 0 && (
        <div
          className="dq-sig-legend"
          style={{
            // Never wider than the table: the legend wraps to its width.
            width: 0,
            minWidth: "100%",
            marginTop: px(4 * scale),
            fontFamily: style.font.family,
            fontSize: px(ptToPx(style.font.sizePt) * scale * 0.85),
            color: style.font.color,
            opacity: 0.75,
            lineHeight: 1.4,
            textAlign: "left",
          }}
        >
          {legendLines.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
      {tooltip &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            className="dq-sig-tooltip"
            style={{
              position: "fixed",
              left: tooltip.left,
              top: tooltip.top,
              transform: `translate(-50%, ${tooltip.above ? "-100%" : "0"})`,
              maxWidth: 320,
              padding: "6px 8px",
              borderRadius: 6,
              background: "rgba(0, 0, 0, 0.85)",
              color: "#ffffff",
              fontSize: 12,
              lineHeight: 1.45,
              pointerEvents: "none",
              zIndex: 1070,
              whiteSpace: "normal",
            }}
          >
            {tooltip.text}
          </div>,
          document.body
        )}
    </div>
  )
}

export default DataTable
